/**
 * <TutorImageCropStep> — « Recadrer l'image » avant que Sam ne la lise (template).
 *
 * L'étape web de `GenerateActivityModal.js` (`ImageCropModal.web.js`) : quand le tuteur dépose une
 * photo (pas un PDF), il garde la zone à analyser. Dans l'ordre du legacy : le titre « Recadrer
 * l'image » et « Ajuste la zone à analyser », la photo et son cadre (`<TutorCropFrame>`), la
 * consigne « Glisse les bords pour ajuster la zone de recadrage », puis « Passer » (la photo
 * entière) et « Valider ». « Annuler » du legacy est la croix de la feuille : une sortie, pas une
 * action.
 *
 * Seulement au web, comme aujourd'hui (le natif n'a pas d'étape de recadrage). Contrôlé : la zone
 * et le traitement en cours arrivent en props, et le geste du cadre rend la nouvelle zone
 * (`onChangeArea`, `<TutorCropFrame>`).
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Modal } from "../../molecules/Modal"
import { TutorCropFrame } from "../../molecules/TutorCropFrame"
import { type UITutorCropArea } from "../../../ports/primary/ui/types/tutorEleves"

export type TutorImageCropStepProps = {
    visible: boolean
    /** La croix : abandonner le dépôt. */
    onClose: () => void
    uri: string
    area: UITutorCropArea
    /** Le geste du cadre : la zone déplacée ou redimensionnée. Absent : le cadre est fixe. */
    onChangeArea?: (area: UITutorCropArea) => void
    /** « Passer » : la photo entière part à l'analyse. */
    onSkip: () => void
    /** « Valider » : la zone retenue part à l'analyse. */
    onConfirm: () => void
    /** La photo se découpe : « Valider » tourne. */
    processing?: boolean
    testID?: string
}

export function TutorImageCropStep(props: TutorImageCropStepProps) {
    const { visible, onClose, uri, area, onChangeArea, onSkip, onConfirm, processing = false, testID } = props

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title="Recadrer l'image"
            fullScreenOnSmall
            footer={
                <Row gap="2" justify="end">
                    <Button label="Passer" variant="ghost" disabled={processing} onPress={onSkip} />
                    <Button label="Valider" iconName="check" loading={processing} onPress={onConfirm} />
                </Row>
            }
            testID={testID}
        >
            <Stack gap="3">
                <Text size="text-md" color="text-muted">
                    Ajuste la zone à analyser
                </Text>
                <TutorCropFrame uri={uri} area={area} onChangeArea={processing ? undefined : onChangeArea} />
                <Text size="text-sm" color="text-muted" center>
                    Glisse les bords pour ajuster la zone de recadrage
                </Text>
            </Stack>
        </Modal>
    )
}
